/**
 * Loading Components
 * Reusable loading states for consistent UX across the application
 */

import { Loader2 } from "lucide-react";
import { Skeleton } from "./skeleton";
import { cn } from "@/lib/utils";
import type { CSSProperties } from "react";

/**
 * Full page loader
 */
export const PageLoader = ({ message = "Loading..." }: { message?: string }) => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="flex flex-col items-center gap-4">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
  </div>
);

/**
 * Branded full page loader
 */
export const BrandedPageLoader = ({
  organizationName,
  tagline,
  logoUrl,
  themeColors,
  message = "Loading site settings...",
}: {
  organizationName: string;
  tagline?: string;
  logoUrl?: string;
  themeColors?: {
    heritage?: string;
    culture?: string;
    accent?: string;
    primary?: string;
  };
  message?: string;
}) => {
  const toHsl = (value: string | undefined, fallbackCssVar: string) => {
    if (!value || value.trim() === "") {
      return `hsl(var(${fallbackCssVar}))`;
    }

    return value.includes("%") ? `hsl(${value})` : value;
  };

  const backgroundStyle: CSSProperties = {
    background: `radial-gradient(circle at top, ${toHsl(themeColors?.culture, "--culture")} 0%, hsl(var(--background)) 60%)`,
  };

  const cardStyle: CSSProperties = {
    borderColor: toHsl(themeColors?.heritage, "--heritage"),
  };

  const spinnerStyle: CSSProperties = {
    color: toHsl(themeColors?.heritage, "--heritage"),
  };

  return (
  <div className="flex min-h-screen items-center justify-center bg-background" style={backgroundStyle}>
    <div
      className="flex w-full max-w-md flex-col items-center gap-4 rounded-xl border bg-background/90 px-6 py-8 text-center shadow-sm backdrop-blur-sm"
      style={cardStyle}
    >
      {logoUrl && logoUrl.trim() !== "" ? (
        <img
          src={logoUrl}
          alt={`${organizationName} logo`}
          className="h-16 w-auto object-contain"
        />
      ) : (
        <div className="h-16 w-16 rounded-full border border-border bg-muted/50" />
      )}
      <div className="space-y-1">
        <h1 className="text-xl font-semibold text-foreground">{organizationName}</h1>
        {tagline && tagline.trim() !== "" && (
          <p className="text-sm text-muted-foreground">{tagline}</p>
        )}
      </div>
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" style={spinnerStyle} />
        <span>{message}</span>
      </div>
    </div>
  </div>
);
};

/**
 * Inline loader
 */
export const InlineLoader = ({ size = "default", className }: { size?: "sm" | "default" | "lg"; className?: string }) => {
  const sizeClasses = {
    sm: "h-4 w-4",
    default: "h-6 w-6",
    lg: "h-8 w-8",
  };

  return (
    <Loader2 className={cn("animate-spin text-primary", sizeClasses[size], className)} />
  );
};

/**
 * Content skeleton loader
 */
export const ContentSkeleton = ({ lines = 6, className }: { lines?: number; className?: string }) => (
  <div className={cn("space-y-3", className)}>
    <Skeleton className="h-8 w-3/4" />
    <Skeleton className="h-4 w-1/2" />
    {[...Array(lines)].map((_, i) => (
      <Skeleton key={i} className="h-4 w-full" />
    ))}
  </div>
);

/**
 * Card skeleton loader
 */
export const CardSkeleton = ({ className }: { className?: string }) => (
  <div className={cn("space-y-4 p-6", className)}>
    <Skeleton className="h-6 w-1/3" />
    <Skeleton className="h-4 w-full" />
    <Skeleton className="h-4 w-5/6" />
    <Skeleton className="h-4 w-4/6" />
  </div>
);

/**
 * Table skeleton loader
 */
export const TableSkeleton = ({ rows = 5, cols = 4 }: { rows?: number; cols?: number }) => (
  <div className="space-y-3">
    {/* Header */}
    <div className="flex gap-4">
      {[...Array(cols)].map((_, i) => (
        <Skeleton key={i} className="h-4 flex-1" />
      ))}
    </div>
    {/* Rows */}
    {[...Array(rows)].map((_, i) => (
      <div key={i} className="flex gap-4">
        {[...Array(cols)].map((_, j) => (
          <Skeleton key={j} className="h-4 flex-1" />
        ))}
      </div>
    ))}
  </div>
);

/**
 * Image skeleton loader
 */
export const ImageSkeleton = ({ aspectRatio = "16/9", className }: { aspectRatio?: string; className?: string }) => (
  <div className={cn("relative overflow-hidden rounded-md", className)} style={{ aspectRatio }}>
    <Skeleton className="absolute inset-0" />
  </div>
);

/**
 * Button loading state
 */
export const ButtonLoader = ({ size = "default" }: { size?: "sm" | "default" | "lg" }) => {
  const sizeClasses = {
    sm: "h-3 w-3",
    default: "h-4 w-4",
    lg: "h-5 w-5",
  };

  return <Loader2 className={cn("animate-spin", sizeClasses[size])} />;
};





